import {
  AlertCircle,
  Block,
  Button,
  CheckCircle,
  GasFee,
  LabelSmall,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  selectIsRecoveryModeEnabled,
  selectPartialRepayStrategy,
  selectUserLoan,
  setPartialRepayStrategy,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import {
  useCollateralizationRatioForExistingLoan,
  useMinimumCollateralRatio,
  useRepayBorrowAmount,
  useUpdatedCollateralBasedOnRatio,
} from "#/shared/hooks"
import { BorrowPartialRepayStrategy } from "#/shared/types"
import { ComponentStepProps } from "#/ui/Modal"

import { BorrowProgress } from "../components"
import BorrowStrategyItem from "../components/BorrowStrategyItem"

export default function RepayStrategy({ onClickContinue }: ComponentStepProps) {
  const [css, theme] = useStyletron()
  const dispatch = useDappDispatch()
  const partialRepaymentStrategy = useDappSelector(selectPartialRepayStrategy)
  const userLoan = useDappSelector(selectUserLoan)
  const isRecoveryModeEnabled = useDappSelector(selectIsRecoveryModeEnabled)
  const { principalRepayAmount } = useRepayBorrowAmount()

  const { minimumCollateralRatio, minimumCollateralRatioLabel } =
    useMinimumCollateralRatio()

  const { collateralizationRatioLabel: prevCollateralizationRatioLabel } =
    useCollateralizationRatioForExistingLoan(
      userLoan?.debtAmount ?? 0n,
      0n,
      userLoan?.collateralAmount ?? 0n,
      0n,
    )

  const {
    collateralizationRatioLabel: newCollateralizationRatioLabel,
    collateralizationRatio: newCollateralizationRatio,
  } = useCollateralizationRatioForExistingLoan(
    userLoan?.debtAmount ?? 0n,
    -principalRepayAmount,
    userLoan?.collateralAmount ?? 0n,
    0n,
  )

  const { additionalCollateralAmountLabel } =
    useUpdatedCollateralBasedOnRatio(-principalRepayAmount)

  const hasInsufficientRatio =
    minimumCollateralRatio !== null &&
    newCollateralizationRatio < minimumCollateralRatio

  const hasError =
    !partialRepaymentStrategy ||
    (partialRepaymentStrategy === BorrowPartialRepayStrategy.INCREASE_RATIO &&
      hasInsufficientRatio)

  return (
    <Block flex={1}>
      <BorrowProgress step={2} stepsCount={4} />
      <Block
        className={css({
          paddingInline: theme.sizing.scale800,
          height: "calc(100% - 44px)",
          display: "flex",
          flexDirection: "column",
        })}
      >
        <Block className={css({ flex: 1 })}>
          <BorrowStrategyItem
            isDisabled={hasInsufficientRatio}
            onChange={() =>
              dispatch(
                setPartialRepayStrategy(
                  BorrowPartialRepayStrategy.INCREASE_RATIO,
                ),
              )
            }
            isChecked={
              partialRepaymentStrategy ===
              BorrowPartialRepayStrategy.INCREASE_RATIO
            }
            title="Increase collateralization ratio"
            description={
              <ParagraphSmall color={theme.colors.contentSecondary}>
                {prevCollateralizationRatioLabel}% →{" "}
                <LabelSmall
                  as="span"
                  color={
                    hasInsufficientRatio
                      ? theme.colors.negative
                      : theme.colors.black
                  }
                >
                  {newCollateralizationRatioLabel}%
                </LabelSmall>
              </ParagraphSmall>
            }
            status={
              <Block
                className={css({
                  display: "flex",
                  alignItems: "center",
                  gap: theme.sizing.scale100,
                  marginTop: theme.sizing.scale300,
                })}
              >
                {hasInsufficientRatio ? (
                  <>
                    <AlertCircle color={theme.colors.negative} size={14} />
                    <ParagraphSmall color={theme.colors.negative}>
                      Collateral ratio {newCollateralizationRatioLabel}%, must
                      be {minimumCollateralRatioLabel}% or higher
                    </ParagraphSmall>
                  </>
                ) : (
                  <>
                    <CheckCircle
                      size={14}
                      color={theme.colors.backgroundAccent}
                    />
                    <ParagraphSmall>Recommended action</ParagraphSmall>
                  </>
                )}
              </Block>
            }
          />
          <BorrowStrategyItem
            isDisabled={isRecoveryModeEnabled}
            onChange={() =>
              dispatch(
                setPartialRepayStrategy(
                  BorrowPartialRepayStrategy.WITHDRAW_COLLATERAL,
                ),
              )
            }
            isChecked={
              partialRepaymentStrategy ===
              BorrowPartialRepayStrategy.WITHDRAW_COLLATERAL
            }
            title="Withdraw collateral"
            description={
              isRecoveryModeEnabled ? (
                <Block
                  display="flex"
                  alignItems="center"
                  marginTop={theme.sizing.scale100}
                >
                  <AlertCircle
                    color={theme.colors.negative}
                    size={theme.sizing.scale600}
                  />
                  <ParagraphSmall
                    marginLeft={theme.sizing.scale100}
                    color={theme.colors.negative}
                  >
                    Unavailable in recovery mode
                  </ParagraphSmall>
                </Block>
              ) : (
                <LabelSmall marginTop={theme.sizing.scale100}>
                  {additionalCollateralAmountLabel}{" "}
                  <CurrencySymbol token="mBTC" />
                </LabelSmall>
              )
            }
            status={
              <Block
                className={css({
                  display: "flex",
                  alignItems: "center",
                  gap: theme.sizing.scale100,
                  marginTop: theme.sizing.scale300,
                })}
              >
                <GasFee size={14} color={theme.colors.contentSecondary} />
                <ParagraphSmall color={theme.colors.contentSecondary}>
                  {"<0.000001"} <CurrencySymbol token="mBTC" />
                </ParagraphSmall>
              </Block>
            }
          />
        </Block>
        <Button
          data-attr="repay_loan-step_3"
          size="large"
          onClick={onClickContinue}
          disabled={hasError}
        >
          Continue
        </Button>
      </Block>
    </Block>
  )
}
